import { useCallback, useState } from 'react';
import type { DomainHelpDnsRecord, DomainHelpQuestionView, DomainHelpResponse } from '@/api/generated';
import { ApiError, backendCode } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

/** A question in the help state: a server row has its `createdAt`; one just asked has none. */
export type HelpQuestion = Omit<DomainHelpQuestionView, 'createdAt'> & { createdAt?: string };

/** The help session as the screen holds it: the server's, with the questions asked since appended. */
export type DomainHelp = Omit<DomainHelpResponse, 'questions'> & { questions?: HelpQuestion[] };

/** Why a follow-up question failed without ending the session: no usable answer, or the AI is busy. */
export type AskFailure = 'noAnswer' | 'busy';

export interface DomainHelpState {
  help: DomainHelp | null;
  error: ApiError | null;
  exhausted: boolean;
  askFailure: AskFailure | null;
  aiUnavailable: boolean;
  starting: boolean;
  asking: boolean;
  questionsLeft: number | null;
  start: (domain: string, records: DomainHelpDnsRecord[], provider?: string) => Promise<void>;
  ask: (question: string) => Promise<boolean>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

function isExhausted(err: ApiError): boolean {
  return backendCode(err) === 'DOMAIN_HELP_EXHAUSTED';
}

export function useDomainHelp(): DomainHelpState {
  const [help, setHelp] = useState<DomainHelp | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const [askFailure, setAskFailure] = useState<AskFailure | null>(null);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  const [starting, setStarting] = useState(false);
  const [asking, setAsking] = useState(false);
  const api = useTenantApi();

  const start = useCallback(
    async (domain: string, records: DomainHelpDnsRecord[], provider?: string) => {
      setStarting(true);
      setError(null);
      setExhausted(false);
      setAskFailure(null);
      setAiUnavailable(false);
      try {
        // No provider chosen: the key is left out (TMMAPI-0167).
        const next = await api.startDomainHelp({
          domain,
          records,
          ...(provider && provider.length > 0 ? { provider } : {}),
        });
        setHelp(next);
      } catch (err) {
        const mapped = toApiError(err);
        setError(mapped);
        throw mapped;
      } finally {
        setStarting(false);
      }
    },
    [api],
  );

  const ask = useCallback(
    async (question: string): Promise<boolean> => {
      const helpId = help?.helpId;
      if (helpId === undefined) return false;
      setAsking(true);
      setError(null);
      setAskFailure(null);
      try {
        const result = await api.askDomainHelp(helpId, { question });
        setHelp((prev) => {
          if (!prev) return prev;
          const prior: HelpQuestion[] = prev.questions ?? [];
          // The ask response has no timestamp, so the appended question has no `createdAt`.
          const added: HelpQuestion = { question, answer: result.answer };
          return {
            ...prev,
            questions: [...prior, added],
            questionsLeft: result.questionsLeft,
          };
        });
        if (result.questionsLeft <= 0) {
          setExhausted(true);
        }
        return true;
      } catch (err) {
        const mapped = toApiError(err);
        const code = backendCode(mapped);
        if (isExhausted(mapped)) {
          setExhausted(true);
          setError(mapped);
          return false;
        }
        if (code === 'AI_DISABLED') {
          setAiUnavailable(true);
          return false;
        }
        if (code === 'AI_NO_SUGGESTION') {
          // Every `reason` reads the same, PROVIDER_REJECTED included.
          setAskFailure('noAnswer');
          return false;
        }
        if (code === 'AI_PROVIDER_UNAVAILABLE') {
          setAskFailure('busy');
          return false;
        }
        setError(mapped);
        throw mapped;
      } finally {
        setAsking(false);
      }
    },
    [api, help?.helpId],
  );

  return {
    help,
    error,
    exhausted,
    askFailure,
    aiUnavailable,
    starting,
    asking,
    questionsLeft: help?.questionsLeft ?? null,
    start,
    ask,
  };
}
